import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, TrendingUp, Clock, DollarSign, Users, Mail, CheckCircle, Calendar, Zap, Shield, Star, X } from 'lucide-react';

const BestPracticesGuide = () => {
  const navigate = useNavigate();

  return (
    <div className="p-4 lg:p-8 pb-20 lg:pb-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/help')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to Help Center</span>
        </button>

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-3">Best Practices for Field Service Success</h1>
          <p className="text-lg text-gray-600">
            Proven strategies and tips to run your business more efficiently and profitably
          </p>
        </div>

        {/* Introduction */}
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-6 mb-8">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center flex-shrink-0">
              <Star className="text-white" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">What Makes a Successful Field Service Business?</h2>
              <p className="text-gray-700">
                Success isn't just about doing great work - it's about running an efficient operation that maximizes your time, 
                keeps customers happy, and ensures you get paid quickly. Here are the practices that separate thriving businesses 
                from struggling ones.
              </p>
            </div>
          </div>
        </div>

        {/* Daily Operations */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Calendar className="text-blue-600" size={24} />
              Daily Operations
            </h2>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Start Every Day with Your Dashboard
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                Open SuddyFlow first thing each morning and check your "Needs Attention" section. This tells you exactly 
                what requires action today - new leads, overdue invoices, follow-ups, jobs ready to invoice.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <strong className="text-blue-900">Pro Tip:</strong> Customize your dashboard to show only what matters to you. 
                Hide widgets you don't use so you can focus on what's important. Click the gear icon in the top-right!
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Use SuddyRoute™ for Daily Scheduling
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                Instead of driving all over town randomly, use route optimization to save time and fuel. SuddyRoute™ 
                can sequence your jobs to maximize revenue per hour - not just shortest distance.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                <strong className="text-green-900">Result:</strong> Most businesses save 30-45 minutes per day and 
                increase daily revenue by 15-20% just by optimizing their route.
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Take "Before & After" Photos
              </h3>
              <p className="text-sm text-gray-700">
                Even if SuddyFlow doesn't store them yet, take before/after photos on every job with your phone. 
                Text them to customers or post on social media. Visual proof of your work is the best marketing you can do - 
                and protects you if there's ever a dispute.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Mark Jobs Complete Same Day
              </h3>
              <p className="text-sm text-gray-700">
                As soon as you finish a job, mark it "Completed" in SuddyFlow. Enter the final amount if it changed. 
                This makes the job appear in your "Ready to Invoice" section immediately - no forgetting to invoice later!
              </p>
            </div>
          </div>
        </div>

        {/* Lead Management */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Users className="text-purple-600" size={24} />
              Lead Management & Sales
            </h2>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Respond to Leads Within 5 Minutes
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                Speed wins customers. When someone requests a quote, they're usually contacting multiple businesses. 
                The first to respond has a 3x higher chance of getting the job.
              </p>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                <strong className="text-amber-900">Reality Check:</strong> If you wait 2 hours to respond, 
                they've probably already hired someone else.
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Set Follow-Up Reminders
              </h3>
              <p className="text-sm text-gray-700">
                Not every lead converts immediately. Set a follow-up date in SuddyFlow (3-7 days out) so you don't forget 
                to check back. Many customers need time to think or get approval from their spouse.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Quote High, Adjust If Needed
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                It's easier to lower a price than raise it. When you first look at a job, quote on the high side. 
                You can always discount if the customer pushes back - but you can't ask for more after giving a lower quote.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                <strong className="text-green-900">Example:</strong> House wash - quote $400. Customer says it's too much? 
                "I can do $350 if you book today." Now they feel like they got a deal, and you still make good money.
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Track Your Conversion Rate
              </h3>
              <p className="text-sm text-gray-700">
                How many leads turn into actual jobs? If you're converting less than 40%, something's wrong - 
                you're either quoting too high, responding too slow, or your sales approach needs work. 
                Aim for 50-70% conversion on warm leads.
              </p>
            </div>
          </div>
        </div>

        {/* Getting Paid */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <DollarSign className="text-green-600" size={24} />
              Getting Paid Faster
            </h2>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Invoice Immediately After Completion
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                Create and send invoices the same day you complete work - ideally within an hour. Customers are most 
                likely to pay when the work is fresh. Waiting weeks = payment delays.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                <strong className="text-red-900">Common Mistake:</strong> Completing jobs on Monday but waiting until 
                Friday to invoice. By then, customers have mentally moved on and payment gets delayed.
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Set Due Dates - They Work!
              </h3>
              <p className="text-sm text-gray-700">
                Always set a due date on invoices. Use 7-14 days for residential, 30 days for commercial. 
                Customers with due dates pay 2x faster than those without. It creates urgency.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Collect Payment On-Site When Possible
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                The absolute best way to get paid fast? Collect when you finish the job. Accept Venmo, Zelle, Cash App, 
                checks, and cash. Have your payment info ready to share.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                <strong className="text-green-900">Script to use:</strong> "You're all set! I can take Venmo, Zelle, 
                check, or cash - whatever works best for you." Then smile and wait. Don't make it awkward.
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Follow Up on Overdue Invoices Immediately
              </h3>
              <p className="text-sm text-gray-700">
                Check your Invoices page every Monday for overdue items. Call or text the same day - don't wait. 
                Most late payments aren't intentional, people just forget. A friendly reminder gets payment within 24 hours.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Make Payment Easy = Get Paid Fast
              </h3>
              <p className="text-sm text-gray-700">
                Accept as many payment methods as possible. The easier you make it to pay, the faster you get paid. 
                Add your Venmo/Zelle info in invoice notes so customers know exactly how to pay you.
              </p>
            </div>
          </div>
        </div>

        {/* Time Management */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Clock className="text-indigo-600" size={24} />
              Time Management
            </h2>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Batch Similar Jobs Together
              </h3>
              <p className="text-sm text-gray-700">
                Group similar services in the same area on the same day. Doing 3 house washes in one neighborhood is 
                way more efficient than driving across town between different types of work.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Block "Admin Time" Daily
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                Set aside 30-60 minutes each morning or evening for admin work: responding to leads, creating invoices, 
                following up on payments. Don't try to do this while driving or working - you'll forget things.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <strong className="text-blue-900">Suggested Schedule:</strong> 7:00-7:30am - Check dashboard, respond to leads. 
                6:00-6:30pm - Invoice completed jobs, record payments.
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Set Realistic Job Durations
              </h3>
              <p className="text-sm text-gray-700">
                Don't pack your schedule too tight. A "2 hour" job usually takes 2.5 hours with drive time, setup, 
                and cleanup. Build buffer time so you're not rushed and stressed all day.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Know When to Say No
              </h3>
              <p className="text-sm text-gray-700">
                Not every lead is worth your time. If a customer is price shopping 10 different companies, wants work 
                done tomorrow for half price, or has red flags - politely decline. Your time is valuable - 
                spend it on good customers who appreciate your work.
              </p>
            </div>
          </div>
        </div>

        {/* Customer Experience */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Star className="text-yellow-600" size={24} />
              Customer Experience
            </h2>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Communicate Proactively
              </h3>
              <p className="text-sm text-gray-700">
                Text customers the day before: "Looking forward to your house wash tomorrow! I'll be there around 10am." 
                Text when you're 15 minutes away: "On my way!" Text when done: "All finished! Check out the photos I sent." 
                This level of communication sets you apart.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Give Customers Portal Access
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                Send portal access links to customers after their first job. They can view their service history and 
                invoices anytime without calling you. This reduces "Where's my invoice?" phone calls by 80%.
              </p>
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-sm">
                <strong className="text-purple-900">Bonus:</strong> Looks super professional and makes you stand out 
                from competitors who just text invoices.
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Ask for Reviews (The Right Way)
              </h3>
              <p className="text-sm text-gray-700">
                Right after completing a job when the customer is happy: "If you're happy with the work, would you mind 
                leaving me a quick Google review? It really helps!" Text them the link. Timing is everything - 
                don't wait days to ask.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Fix Problems Immediately
              </h3>
              <p className="text-sm text-gray-700">
                If a customer isn't happy, drop everything and fix it that same day. A $100 return trip costs less than 
                a bad review. Handle complaints quickly and professionally - they often turn into your best referral sources.
              </p>
            </div>
          </div>
        </div>

        {/* Growth & Marketing */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <TrendingUp className="text-green-600" size={24} />
              Growth & Marketing
            </h2>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Referrals Are Your Best Leads
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                Ask every happy customer: "Do any of your neighbors need this service? I'd love to help them out too." 
                Referral leads convert at 70-80% vs. 40-50% for cold leads - and they're free.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-sm">
                <strong className="text-green-900">Referral Bonus Idea:</strong> "If you refer a neighbor and they book, 
                I'll give you $25 off your next service." Works incredibly well.
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Post Before & After Photos Everywhere
              </h3>
              <p className="text-sm text-gray-700">
                Facebook, Instagram, Nextdoor, Google Business Profile - post your work. Before/after photos get 10x 
                more engagement than any other content. Do this weekly, not just when you remember.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Focus on Repeat Customers
              </h3>
              <p className="text-sm text-gray-700">
                It's 5x easier to get repeat business than find new customers. Keep a list of customers who need annual 
                service (like gutter cleaning) and reach out 11 months after their last job. Easy money.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Yard Signs & Truck Lettering
              </h3>
              <p className="text-sm text-gray-700">
                Put a yard sign at every job site: "Your Neighbors Trust Us!" with your phone number. Get truck lettering 
                with your business name, phone, and website. You're driving a billboard - use it!
              </p>
            </div>
          </div>
        </div>

        {/* Business Management */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Shield className="text-red-600" size={24} />
              Business Management & Protection
            </h2>
          </div>
          <div className="p-6 space-y-5">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Review Your Numbers Weekly
              </h3>
              <p className="text-sm text-gray-700 mb-2">
                Every Monday, check your dashboard: Total Revenue, Collection Rate, Overdue Invoices. Are you on track 
                for your monthly goal? Catching problems early is key.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
                <strong className="text-blue-900">Goal to Track:</strong> Average revenue per job. If this is dropping, 
                either your prices are too low or you're taking too many small jobs.
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Maintain Insurance & Licensing
              </h3>
              <p className="text-sm text-gray-700">
                Keep your general liability insurance current and any required licenses up to date. One lawsuit can 
                destroy your business. Insurance is cheap compared to legal bills.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Separate Business & Personal Finances
              </h3>
              <p className="text-sm text-gray-700">
                Use a separate bank account for business. Makes taxes way easier and looks more professional. 
                Pay yourself a "salary" from the business account to your personal account - don't mix them.
              </p>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                <CheckCircle size={18} className="text-green-600" />
                Save for Quarterly Taxes
              </h3>
              <p className="text-sm text-gray-700">
                Set aside 25-30% of your profit for taxes. Put it in a separate savings account and don't touch it. 
                Quarterly tax payments are due April 15, June 15, September 15, and January 15. Don't get caught off guard!
              </p>
            </div>
          </div>
        </div>

        {/* Common Mistakes to Avoid */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">🚫 Common Mistakes to Avoid</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3">
              <X size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-900">Underpricing Your Services</p>
                <p className="text-sm text-red-800">
                  You're not Walmart. Don't compete on price. Compete on quality, speed, and service. 
                  If you're the cheapest, customers wonder what's wrong with your work.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <X size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-900">Not Following Up on Leads</p>
                <p className="text-sm text-red-800">
                  80% of sales happen after the 5th contact. Most businesses give up after one try. 
                  Set follow-up reminders and actually follow up!
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <X size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-900">Doing Everything Yourself</p>
                <p className="text-sm text-red-800">
                  At some point, your time becomes too valuable to do $15/hour tasks. Hire help when your schedule 
                  is consistently full and you're turning down work.
                </p>
              </div>
            </div>
            
            <div className="flex items-start gap-3">
              <X size={18} className="text-red-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-red-900">Ignoring Unhappy Customers</p>
                <p className="text-sm text-red-800">
                  One bad review costs you 10+ future customers. Address complaints immediately, 
                  fix problems for free, and turn haters into fans.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Success Metrics */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-4">📊 Key Metrics to Track</h2>
          <p className="text-sm text-gray-600 mb-4">
            These numbers tell you if your business is healthy:
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="font-semibold text-gray-900 mb-1">Lead Conversion Rate</p>
              <p className="text-2xl font-bold text-green-600 mb-1">50-70%</p>
              <p className="text-xs text-gray-600">Percentage of leads that become paying customers</p>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="font-semibold text-gray-900 mb-1">Collection Rate</p>
              <p className="text-2xl font-bold text-green-600 mb-1">90%+</p>
              <p className="text-xs text-gray-600">Percentage of invoices that get paid</p>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="font-semibold text-gray-900 mb-1">Average Days to Payment</p>
              <p className="text-2xl font-bold text-green-600 mb-1">&lt;7 days</p>
              <p className="text-xs text-gray-600">How long it takes to get paid after invoicing</p>
            </div>
            
            <div className="border border-gray-200 rounded-lg p-4">
              <p className="font-semibold text-gray-900 mb-1">Repeat Customer Rate</p>
              <p className="text-2xl font-bold text-green-600 mb-1">30%+</p>
              <p className="text-xs text-gray-600">Percentage of customers who hire you again</p>
            </div>
          </div>
        </div>

        {/* Final Thoughts */}
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-bold text-gray-900 mb-3">The Bottom Line</h2>
          <p className="text-gray-700 mb-3">
            Success in field services isn't about working harder - it's about working smarter. Use SuddyFlow to handle 
            the admin work so you can focus on what you do best: delivering great service and growing your business.
          </p>
          <p className="text-gray-700">
            The businesses that thrive are the ones that respond fast, communicate well, get paid quickly, and 
            treat customers right. Follow these practices and you'll be ahead of 90% of your competition.
          </p>
        </div>

        {/* Need Help */}
        <div className="bg-gradient-to-r from-primary-50 to-primary-100 border border-primary-200 rounded-lg p-6 text-center mb-8">
          <h3 className="text-xl font-bold text-gray-900 mb-2">Need Personalized Advice?</h3>
          <p className="text-gray-700 mb-4">
            Every business is different. We're happy to chat about your specific situation!
          </p>
          <a
            href="mailto:support@suddyflow.com"
            className="btn btn-primary inline-flex items-center gap-2"
          >
            <Mail size={18} />
            Email Support
          </a>
        </div>

        {/* Back to Help Center */}
        <div className="text-center">
          <button
            onClick={() => navigate('/help')}
            className="text-primary-600 hover:text-primary-700 font-medium"
          >
            ← Back to Help Center
          </button>
        </div>
      </div>
    </div>
  );
};

export default BestPracticesGuide;